import {
  Text,
  IconArrowUpRight,
  Link,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  GreyWhite,
  Primary14SemiBold,
  Primary13Regular,
  Badge,
  PopoverWrapper,
  RadioButtonGroup,
  SaveStatus,
  Button,
  PageTitle,
  Box,
  Flex,
  Grid,
  Tooltip
} from '@sequencehq/core-components'
import { ExecuteQuoteModal } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote'
import { ConfirmPublishQuoteModal } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/modals/PublishQuoteModal'
import {
  useQuoteEditorHeader,
  ViewMode
} from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/useQuoteEditorHeader'
import { QuoteEditorKebabMenu } from './QuoteEditorKebabMenu/QuoteEditorKebabMenu'
import { PopoverPublishedQuote } from './PopoverPublishedQuote/PopoverPublishedQuote'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { useEmbeddedSigningFlow } from '../hooks'
import { PublishQuoteModal } from './modals/PublishQuoteModalV2'
import { BadgeNeedsReview } from './NeedsReview.badge'

/**
 * How wide the publish button sits while it spins. It has no label to size
 * itself from, so without this it shrinks to the spinner and everything beside
 * it shifts along when the first answer lands. Matches the width of "Publish",
 * which is what it says most of the time.
 */
const PUBLISH_BUTTON_CHECKING_WIDTH = '66px'

const styles = {
  title: {
    ...Primary14SemiBold,
    color: GreyGrey80
  },
  archivedTitle: {
    ...Primary14SemiBold,
    color: GreyGrey60
  }
}

const viewModeOptions = [
  { value: ViewMode.Edit, label: 'Edit' },
  { value: ViewMode.Preview, label: 'Preview' }
]

const readOnlyViewModeOptions = [{ value: ViewMode.Preview, label: 'Preview' }]

export const QuoteEditorHeader = () => {
  const { isEnabled: isEmbeddedSigningEnabled } = useEmbeddedSigningFlow()
  const headerHook = useQuoteEditorHeader()

  const { can } = usePermissions()
  const pageTitle = headerHook.title || 'Quote'

  return (
    <>
      <PageTitle title={[pageTitle, 'Quotes']} />
      <Grid
        gridTemplateColumns="1fr auto"
        width="100%"
        height="48px"
        alignItems="center"
        borderBottom={`1px solid ${GreyGrey30}`}
        backgroundColor={GreyWhite}
        padding="0 8px"
        position="relative"
      >
        <Box>
          <Flex gap="16px" alignItems="center">
            {can('org.quote:update') ? (
              <RadioButtonGroup
                options={viewModeOptions.map(option => ({
                  ...option,
                  disabled:
                    option.value === ViewMode.Preview &&
                    headerHook.features.preview.disabled
                }))}
                value={headerHook.viewMode.current}
                onChange={headerHook.viewMode.onChange}
                data-testid="quoteEditor.viewMode"
              />
            ) : (
              <RadioButtonGroup
                options={readOnlyViewModeOptions}
                value={ViewMode.Preview}
                onChange={() => null}
                data-testid="quoteEditor.viewMode"
              />
            )}
            <SaveStatus
              isSaving={headerHook.syncing}
              data-testId="quote.save-status"
            />
          </Flex>
        </Box>

        <Flex
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          gap="4px"
          alignItems="center"
          maxWidth="50%"
        >
          <Text
            {...Primary13Regular}
            color={GreyGrey60}
            isTruncated
            display="flex"
            alignItems="center"
          >
            <Text
              as={Link.Unstyled}
              color="inherit"
              to="/quotes"
              display="inline-block"
            >
              Quotes
            </Text>
            <Text as="span" mx="4px">
              /
            </Text>
            <Text
              as="span"
              style={
                headerHook.isArchived ? styles.archivedTitle : styles.title
              }
              maxWidth="300px"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              display="inline-block"
              title={headerHook.title}
            >
              {headerHook.title}
            </Text>
          </Text>

          {headerHook.badgeNeedsReview.visible && (
            <Box marginLeft="4px">
              <BadgeNeedsReview quoteId={headerHook.quoteId} />
            </Box>
          )}

          {headerHook.badgeUnpublishedChanges.visible && (
            <Badge
              sentiment="highlight"
              size="md"
              textTransform="none"
              data-testid="quote.header.unpublishedChangesBadge"
            >
              Unpublished changes
            </Badge>
          )}
          {headerHook.badgeIsExpired.visible && (
            <Badge sentiment="error" size="md" textTransform="none">
              Expired
            </Badge>
          )}
        </Flex>

        <Flex gap="8px">
          {headerHook.hasBeenPublished && !headerHook.isArchived && (
            <Button
              variant="secondary"
              onClick={() => window.open(headerHook.portalUrl, '_blank')}
              leadingIcon={<IconArrowUpRight color="inherit" />}
              data-testid="quote.viewPublishedQuote"
            >
              View published quote
            </Button>
          )}

          {headerHook.features.publish.visible && (
            <PopoverWrapper
              popover={
                <PopoverPublishedQuote
                  onClose={headerHook.publishedLinkPopover.onClose}
                />
              }
              isOpen={headerHook.publishedLinkPopover.isOpen}
              placement="bottom-end"
            >
              <Tooltip
                isDisabled={!headerHook.features.publish.tooltip}
                label={headerHook.features.publish.tooltip}
                data-testid="quote.header.buttonPublish.tooltip"
              >
                <ProtectedButton permission="org.quote:publish">
                  <Button
                    variant="primary"
                    onClick={headerHook.features.publish.onClick}
                    disabled={headerHook.features.publish.disabled}
                    submitting={headerHook.features.publish.isLoading}
                    // A spinner on its own. The button has nothing to say until
                    // the first answer lands, and a guess would be worse than
                    // the wait.
                    submittingText=""
                    width={
                      headerHook.features.publish.isLoading
                        ? PUBLISH_BUTTON_CHECKING_WIDTH
                        : undefined
                    }
                    data-testid="quote.header.buttonPublish"
                  >
                    {headerHook.features.publish.text}
                  </Button>
                </ProtectedButton>
              </Tooltip>
            </PopoverWrapper>
          )}

          {headerHook.features.execute.visible && (
            <Tooltip
              isDisabled={!headerHook.features.execute.tooltip}
              label={headerHook.features.execute.tooltip}
              data-testid="quote.header.buttonExecute.tooltip"
            >
              <Button
                variant="primary"
                onClick={headerHook.features.execute.onClick}
                disabled={headerHook.features.execute.disabled}
                data-testid="quote.header.buttonExecute"
              >
                {headerHook.features.execute.text}
              </Button>
            </Tooltip>
          )}

          <QuoteEditorKebabMenu />
        </Flex>
      </Grid>

      {isEmbeddedSigningEnabled && headerHook.modals.publishEmbedded.active && (
        <PublishQuoteModal
          onClose={headerHook.modals.publishEmbedded.onClose}
          onSubmit={headerHook.modals.publishEmbedded.onConfirm}
          isQuoteSyncing={headerHook.modals.publishEmbedded.isQuoteSyncing}
          submitting={headerHook.modals.publishEmbedded.submitting}
          isApprovalRequired={
            headerHook.modals.publishEmbedded.isApprovalRequired
          }
          isSimulationPending={
            headerHook.modals.publishEmbedded.isSimulationPending
          }
          isAutoRejected={headerHook.modals.publishEmbedded.isAutoRejected}
        />
      )}

      {!isEmbeddedSigningEnabled && headerHook.modals.publish.active && (
        <ConfirmPublishQuoteModal
          submitting={headerHook.modals.publish.submitting}
          onClose={headerHook.modals.publish.onClose}
          onConfirm={headerHook.modals.publish.onConfirm}
          hasBeenPublished={headerHook.hasBeenPublished}
          isReadyToSign={headerHook.isReadyToSign}
          recipientCountText={headerHook.modals.publish.recipientCountText}
          republishingWithoutChanges={
            !headerHook.badgeUnpublishedChanges.visible &&
            headerHook.badgeIsExpired.visible
          }
        />
      )}
      {headerHook.modals.execute.active && (
        <ExecuteQuoteModal
          submitting={headerHook.modals.execute.submitting}
          onClose={headerHook.modals.execute.onClose}
          onConfirm={headerHook.modals.execute.onConfirm}
        />
      )}
    </>
  )
}
